/**
 * Iter139 — Global HTTP interceptor.
 *
 * Monkey-patches window.fetch so that any 423 Locked response coming from a
 * case mutation API surfaces a single, clear sonner toast with a "Go to
 * Acceptance Form" deep-link. The original Response object is returned
 * unchanged so every existing call-site keeps working.
 *
 * This is intentionally kept as a side-effect import loaded from App.js so
 * the hundreds of raw `fetch(...)` call-sites in the codebase don't need to
 * be refactored.
 */
import { toast } from 'sonner';

let installed = false;
let lastShownAt = 0;

const CASE_ID_RE = /\/api\/cases\/([A-Za-z0-9_-]+)/;

function extractCaseId(url) {
  try {
    const u = typeof url === 'string' ? url : (url?.url || '');
    const m = u.match(CASE_ID_RE);
    return m ? m[1] : null;
  } catch { return null; }
}

export function installHttpInterceptor() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    try {
      if (response && response.status === 423) {
        const urlStr = typeof input === 'string' ? input : (input?.url || '');
        // Only surface a banner for the case-acceptance lock; other 423s
        // (training lockouts, etc.) are handled by their own screens.
        if (urlStr.includes('/api/cases/')) {
          const now = Date.now();
          if (now - lastShownAt > 3000) {
            lastShownAt = now;
            const caseId = extractCaseId(urlStr);
            // Clone the response so we can read the detail without
            // consuming the body for the original caller.
            let detail = 'This case is locked until you sign the Case Acceptance Agreement.';
            try {
              const clone = response.clone();
              const data = await clone.json();
              if (data && data.detail) detail = data.detail;
            } catch { /* ignore */ }
            toast.error('Case locked', {
              description: detail,
              duration: 8000,
              action: {
                label: 'Go to Acceptance Form',
                onClick: () => {
                  const target = caseId ? `/case-acceptance?case=${caseId}` : '/case-acceptance';
                  window.location.href = target;
                },
              },
            });
          }
        }
      }
    } catch { /* interceptor must never throw */ }
    return response;
  };
}

export default installHttpInterceptor;
